import React, { useCallback, useEffect, useState } from 'react';
import {
  Inbox,
  Users,
  Palette,
  Send,
  Trash2,
  Plus,
  Loader2,
  Check,
  X,
  Mail,
  FileText,
  History,
} from 'lucide-react';
import { useFirm } from '../hooks/useFirm';
import {
  firmService,
  FirmBranding,
  FirmClient,
  ClientDocket,
  ClientRecipient,
  QueueItem,
  FILING_TYPE_OPTIONS,
} from '../lib/firmService';
import { SimpleDocketAutocomplete } from '../components/SimpleDocketAutocomplete';

const DEFAULT_ACCENT = '#14324f';
const ALL_FILING_TYPES = FILING_TYPE_OPTIONS.map((o) => o.value);
type Tab = 'queue' | 'sent' | 'clients' | 'branding';

export const FirmConsolePage: React.FC = () => {
  const { firm } = useFirm();
  const [tab, setTab] = useState<Tab>('queue');
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [stats, setStats] = useState({ clients: 0, pending: 0, sent: 0 });
  const firmId = firm?.firm_id;

  const refreshStats = useCallback(async () => {
    if (!firmId) return;
    setStats(await firmService.getStats(firmId));
  }, [firmId]);

  useEffect(() => {
    if (!firmId) return;
    firmService.getBranding(firmId).then((b) => {
      if (b?.primary_color) setAccent(b.primary_color);
    });
    refreshStats();
  }, [firmId, refreshStats]);

  if (!firm || !firmId) return null;

  const tabs: { key: Tab; label: string; icon: any; badge?: number }[] = [
    { key: 'queue', label: 'Review & Send', icon: Inbox, badge: stats.pending || undefined },
    { key: 'sent', label: 'Sent', icon: History, badge: stats.sent || undefined },
    { key: 'clients', label: 'Clients', icon: Users, badge: stats.clients || undefined },
    { key: 'branding', label: 'Branding', icon: Palette },
  ];

  return (
    <div className="min-h-screen bg-cg-cream">
      {/* Header band */}
      <div className="border-b border-gray-200 bg-white">
        <div className="px-6 sm:px-8 pt-8 pb-5">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: accent }} />
            <span className="text-xs font-semibold tracking-wide uppercase text-gray-400">
              Client engagement console
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{firm.firm.name}</h1>
          <div className="flex gap-8 mt-4">
            <Stat label={stats.clients === 1 ? 'Client' : 'Clients'} value={stats.clients} />
            <Stat label="Ready to review" value={stats.pending} accent={accent} />
            <Stat label="Sent" value={stats.sent} />
          </div>
        </div>
        <div className="px-6 sm:px-8">
          <div className="flex gap-1">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                    active ? 'text-gray-900' : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                  {t.badge ? (
                    <span className="ml-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {t.badge}
                    </span>
                  ) : null}
                  {active && (
                    <span
                      className="absolute left-3 right-3 -bottom-px h-0.5 rounded-full"
                      style={{ background: accent }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-6 sm:px-8 py-8">
        {tab === 'queue' && <ReviewSendTab firmId={firmId} accent={accent} onChange={refreshStats} />}
        {tab === 'sent' && <SentTab firmId={firmId} accent={accent} />}
        {tab === 'clients' && <ClientsTab firmId={firmId} accent={accent} onChange={refreshStats} />}
        {tab === 'branding' && (
          <BrandingTab firmId={firmId} onSaved={(c) => c && setAccent(c)} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Review & Send — master/detail
// ---------------------------------------------------------------------------

const ReviewSendTab: React.FC<{ firmId: string; accent: string; onChange: () => void }> = ({
  firmId,
  accent,
  onChange,
}) => {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [clients, setClients] = useState<Record<string, FirmClient>>({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [coverNote, setCoverNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [q, cs] = await Promise.all([
      firmService.listPendingQueue(firmId),
      firmService.listClients(firmId),
    ]);
    setItems(q);
    setClients(Object.fromEntries(cs.map((c) => [c.id, c])));
    setSelectedId((prev) => (prev && q.some((i) => i.id === prev) ? prev : q[0]?.id ?? null));
    setLoading(false);
  }, [firmId]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = items.find((i) => i.id === selectedId) || null;

  const send = async () => {
    if (!selected) return;
    setBusy(true);
    setMsg(null);
    const rest = items.filter((i) => i.id !== selected.id);
    try {
      await firmService.sendQueued(selected.id, coverNote.trim() || null);
      setItems(rest);
      setSelectedId(rest[0]?.id ?? null);
      setCoverNote('');
      setMsg({
        type: 'ok',
        text: `Sent to ${clients[selected.client_id]?.primary_email || 'client'}.`,
      });
      onChange();
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.message || 'Send failed' });
    } finally {
      setBusy(false);
    }
  };

  const discard = async (id: string) => {
    setBusy(true);
    const rest = items.filter((i) => i.id !== id);
    try {
      await firmService.discardQueued(id);
      setItems(rest);
      if (selectedId === id) setSelectedId(rest[0]?.id ?? null);
      onChange();
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner />;
  if (!items.length)
    return (
      <EmptyState
        icon={Inbox}
        title="You're all caught up"
        body="When new filings land on your clients' dockets, a prepared, firm-branded digest appears here for you to review and send."
      />
    );

  return (
    <div className="flex gap-6 items-start">
      {/* list */}
      <div className="w-[340px] shrink-0 space-y-2">
        {msg && (
          <div className={`text-sm mb-2 ${msg.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
            {msg.text}
          </div>
        )}
        {items.map((item) => {
          const cl = clients[item.client_id];
          const active = item.id === selectedId;
          return (
            <button
              key={item.id}
              onClick={() => {
                setSelectedId(item.id);
                setCoverNote('');
              }}
              className={`w-full text-left p-3.5 rounded-xl bg-white transition-all ${
                active ? 'border-2 shadow-sm' : 'border border-gray-200 hover:border-gray-300'
              }`}
              style={active ? { borderColor: accent } : {}}
            >
              <div className="flex items-center gap-3">
                <Avatar name={cl?.display_name || '?'} accent={accent} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 truncate">
                    {cl?.display_name || 'Client'}
                  </div>
                  <div className="text-xs text-gray-400 truncate">
                    {cl?.primary_email || 'no recipient'}
                  </div>
                </div>
              </div>
              <div className="mt-2.5 flex items-center gap-2 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {item.payload?.total_filings ?? 0} filing
                  {(item.payload?.total_filings ?? 0) === 1 ? '' : 's'}
                </span>
                <span className="text-gray-300">·</span>
                <span className="font-mono">{(item.payload?.dockets || []).join(', ')}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* detail */}
      <div className="flex-1 min-w-0">
        {selected ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-500 flex items-center gap-1.5">
                <Mail className="w-4 h-4" /> Sends to{' '}
                <span className="font-medium text-gray-800">
                  {clients[selected.client_id]?.primary_email || 'no recipient'}
                </span>
              </div>
              <button
                onClick={() => discard(selected.id)}
                disabled={busy}
                className="text-xs text-gray-400 hover:text-red-500 inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Discard
              </button>
            </div>
            <div className="p-4 bg-gray-50">
              <iframe
                title="digest preview"
                srcDoc={selected.payload?.html || '<p>No preview</p>'}
                sandbox=""
                className="w-full h-[540px] rounded-lg border border-gray-200 bg-white"
              />
            </div>
            <div className="p-5 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Add a personal note{' '}
                <span className="text-gray-400 font-normal">
                  (optional — appears at the top, in your voice)
                </span>
              </label>
              <textarea
                value={coverNote}
                onChange={(e) => setCoverNote(e.target.value)}
                rows={2}
                placeholder="Add a quick note for your client…"
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
              <button
                onClick={send}
                disabled={busy}
                className="mt-3 inline-flex items-center gap-2 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50"
                style={{ background: accent }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send digest
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sent — history of what went out
// ---------------------------------------------------------------------------

const fmtSentDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const SentTab: React.FC<{ firmId: string; accent: string }> = ({ firmId, accent }) => {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [clients, setClients] = useState<Record<string, FirmClient>>({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const [q, cs] = await Promise.all([
        firmService.listSentDigests(firmId),
        firmService.listClients(firmId),
      ]);
      if (!active) return;
      setItems(q);
      setClients(Object.fromEntries(cs.map((c) => [c.id, c])));
      setSelectedId(q[0]?.id ?? null);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [firmId]);

  const selected = items.find((i) => i.id === selectedId) || null;

  if (loading) return <Spinner />;
  if (!items.length)
    return (
      <EmptyState
        icon={History}
        title="Nothing sent yet"
        body="Digests you send to clients will appear here — a running record of what went out, to whom, and when."
      />
    );

  return (
    <div className="flex gap-6 items-start">
      <div className="w-[340px] shrink-0 space-y-2">
        {items.map((item) => {
          const cl = clients[item.client_id];
          const active = item.id === selectedId;
          return (
            <button
              key={item.id}
              onClick={() => setSelectedId(item.id)}
              className={`w-full text-left p-3.5 rounded-xl bg-white transition-all ${
                active ? 'border-2 shadow-sm' : 'border border-gray-200 hover:border-gray-300'
              }`}
              style={active ? { borderColor: accent } : {}}
            >
              <div className="flex items-center gap-3">
                <Avatar name={cl?.display_name || '?'} accent={accent} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 truncate">
                    {cl?.display_name || 'Client'}
                  </div>
                  <div className="text-xs text-gray-400 truncate">{cl?.primary_email || ''}</div>
                </div>
              </div>
              <div className="mt-2.5 flex items-center gap-2 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <Check className="w-3 h-3" style={{ color: accent }} /> Sent{' '}
                  {fmtSentDate(item.sent_at)}
                </span>
                <span className="text-gray-300">·</span>
                <span>{item.payload?.total_filings ?? 0} filings</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex-1 min-w-0">
        {selected && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-500 flex items-center gap-1.5">
                <Mail className="w-4 h-4" /> Sent to{' '}
                <span className="font-medium text-gray-800">
                  {clients[selected.client_id]?.primary_email || ''}
                </span>
              </div>
              <div className="text-xs text-gray-500 inline-flex items-center gap-1">
                <Check className="w-3.5 h-3.5" style={{ color: accent }} />
                {fmtSentDate(selected.sent_at)}
              </div>
            </div>
            <div className="p-4 bg-gray-50">
              <iframe
                title="sent digest"
                srcDoc={selected.payload?.sent_html || selected.payload?.html || ''}
                sandbox=""
                className="w-full h-[560px] rounded-lg border border-gray-200 bg-white"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

const ClientsTab: React.FC<{ firmId: string; accent: string; onChange: () => void }> = ({
  firmId,
  accent,
  onChange,
}) => {
  const [clients, setClients] = useState<FirmClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [business, setBusiness] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setClients(await firmService.listClients(firmId));
    setLoading(false);
  }, [firmId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    setErr(null);
    try {
      await firmService.createClient(firmId, {
        display_name: name.trim(),
        contact_name: contact.trim() || null,
        primary_email: email.trim() || null,
        business_description: business.trim() || null,
      });
      setName('');
      setContact('');
      setEmail('');
      setBusiness('');
      setAdding(false);
      load();
      onChange();
    } catch (e: any) {
      setErr(e?.message || 'Could not add client');
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="max-w-3xl">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-500">
          {clients.length} client{clients.length === 1 ? '' : 's'}
        </div>
        <button
          onClick={() => setAdding((v) => !v)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-white px-3.5 py-2 rounded-lg hover:opacity-90"
          style={{ background: accent }}
        >
          <Plus className="w-4 h-4" />
          Add client
        </button>
      </div>

      {adding && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3 mb-4 shadow-sm">
          {err && <div className="text-sm text-red-600">{err}</div>}
          <Field label="Client (company)" value={name} onChange={setName} placeholder="Acme Telecom, Inc." />
          <Field
            label="Contact name (who the email greets)"
            value={contact}
            onChange={setContact}
            placeholder="Jane Doe"
          />
          <Field
            label="Recipient email"
            value={email}
            onChange={setEmail}
            placeholder="jane@client.com"
          />
          <Field
            label="What they do (optional — seeds future relevance)"
            value={business}
            onChange={setBusiness}
            placeholder="Regional wireless carrier"
          />
          <div className="flex gap-2">
            <button
              onClick={add}
              className="text-white text-sm font-semibold px-4 py-2 rounded-lg"
              style={{ background: accent }}
            >
              Add client
            </button>
            <button onClick={() => setAdding(false)} className="text-sm text-gray-500 px-3 py-2">
              Cancel
            </button>
          </div>
        </div>
      )}

      {!clients.length && !adding ? (
        <EmptyState
          icon={Users}
          title="No clients yet"
          body="Add your first client, assign the dockets they care about, and CommsGenie prepares a branded digest for you to review and send."
        />
      ) : (
        <div className="space-y-2.5">
          {clients.map((c) => (
            <ClientRow
              key={c.id}
              firmId={firmId}
              client={c}
              accent={accent}
              expanded={expanded === c.id}
              onToggle={() => setExpanded((e) => (e === c.id ? null : c.id))}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ClientRow: React.FC<{
  firmId: string;
  client: FirmClient;
  accent: string;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
}> = ({ firmId, client, accent, expanded, onToggle, onChanged }) => {
  const [dockets, setDockets] = useState<ClientDocket[]>([]);
  const [newDocket, setNewDocket] = useState('');
  // All types active by default (stored empty = all). Attorney deselects to exclude.
  const [types, setTypes] = useState<string[]>(
    client.filing_types?.length ? client.filing_types : ALL_FILING_TYPES,
  );
  const [busy, setBusy] = useState(false);
  // Editable client details
  const [company, setCompany] = useState(client.display_name);
  const [contactName, setContactName] = useState(client.contact_name || '');
  const [primaryEmail, setPrimaryEmail] = useState(client.primary_email || '');
  const [savingInfo, setSavingInfo] = useState(false);
  const [savedInfo, setSavedInfo] = useState(false);
  // Additional recipients (the company's CC list)
  const [recipients, setRecipients] = useState<ClientRecipient[]>([]);
  const [newRecipient, setNewRecipient] = useState('');
  // Initial coverage window (how far back the first digest reaches)
  const [coverageMsg, setCoverageMsg] = useState('');

  const applyInitialWindow = async (val: string) => {
    if (!val) return;
    setCoverageMsg('');
    try {
      const days = val === 'all' ? null : parseInt(val, 10);
      const n = await firmService.setClientInitialWindow(client.id, days);
      setCoverageMsg(
        n === 0
          ? 'Add a docket first, then set coverage.'
          : `Set on ${n} docket${n === 1 ? '' : 's'}.`,
      );
    } catch (e) {
      setCoverageMsg(e instanceof Error ? e.message : 'Could not set coverage');
    }
  };

  const loadDockets = useCallback(async () => {
    setDockets(await firmService.listClientDockets(client.id));
  }, [client.id]);
  const loadRecipients = useCallback(async () => {
    setRecipients(await firmService.listClientRecipients(client.id));
  }, [client.id]);

  useEffect(() => {
    if (expanded) {
      loadDockets();
      loadRecipients();
    }
  }, [expanded, loadDockets, loadRecipients]);

  const saveInfo = async () => {
    if (!company.trim()) return;
    setSavingInfo(true);
    setSavedInfo(false);
    try {
      await firmService.updateClient(client.id, {
        display_name: company.trim(),
        contact_name: contactName.trim() || null,
        primary_email: primaryEmail.trim() || null,
      });
      setSavedInfo(true);
      onChanged();
    } finally {
      setSavingInfo(false);
    }
  };

  const addRecipient = async () => {
    const e = newRecipient.trim();
    if (!e) return;
    setBusy(true);
    try {
      await firmService.addClientRecipient(firmId, client.id, e);
      setNewRecipient('');
      await loadRecipients();
    } finally {
      setBusy(false);
    }
  };
  const removeRecipient = async (id: string) => {
    await firmService.removeClientRecipient(id);
    await loadRecipients();
  };

  const toggleType = async (v: string) => {
    const next = types.includes(v) ? types.filter((t) => t !== v) : [...types, v];
    if (next.length === 0) return; // keep at least one type active
    setTypes(next);
    // all selected = canonical "all" (store empty); otherwise the subset
    const toStore = next.length === ALL_FILING_TYPES.length ? [] : next;
    await firmService.updateClient(client.id, { filing_types: toStore });
  };

  const addDocketValue = async (v: string) => {
    const dn = (v || '').trim();
    if (!dn) return;
    setBusy(true);
    try {
      await firmService.addClientDocket(firmId, client.id, dn, 'daily');
      setNewDocket('');
      await loadDockets();
    } finally {
      setBusy(false);
    }
  };

  const removeDocket = async (id: string) => {
    await firmService.removeClientDocket(id);
    await loadDockets();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button onClick={onToggle} className="w-full text-left p-4 flex items-center gap-3">
        <Avatar name={client.display_name} accent={accent} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-gray-900 truncate">{client.display_name}</div>
          <div className="text-xs text-gray-400 truncate">
            {client.primary_email || 'no recipient'} ·{' '}
            {client.filing_types?.length ? client.filing_types.join(', ') : 'all filing types'}
          </div>
        </div>
        <span className="text-xs text-gray-400 shrink-0">{expanded ? 'Close' : 'Manage'}</span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-5 bg-gray-50/50">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Client details</div>
            <div className="grid sm:grid-cols-3 gap-2">
              <input
                value={company}
                onChange={(e) => { setCompany(e.target.value); setSavedInfo(false); }}
                placeholder="Company"
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
              <input
                value={contactName}
                onChange={(e) => { setContactName(e.target.value); setSavedInfo(false); }}
                placeholder="Contact name"
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
              <input
                value={primaryEmail}
                onChange={(e) => { setPrimaryEmail(e.target.value); setSavedInfo(false); }}
                placeholder="Recipient email"
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
            </div>
            <button
              onClick={saveInfo}
              disabled={savingInfo}
              className="mt-2 inline-flex items-center gap-1.5 text-white text-sm px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50"
              style={{ background: accent }}
            >
              {savingInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : savedInfo ? <Check className="w-4 h-4" /> : null}
              {savedInfo ? 'Saved' : 'Save details'}
            </button>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              Filing types <span className="text-gray-400 font-normal">(all active — deselect any to exclude)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {FILING_TYPE_OPTIONS.map((o) => {
                const on = types.includes(o.value);
                return (
                  <button
                    key={o.value}
                    onClick={() => toggleType(o.value)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      on ? 'text-white border-transparent' : 'bg-white text-gray-600 border-gray-300'
                    }`}
                    style={on ? { background: accent } : {}}
                  >
                    {on && <Check className="w-3 h-3 inline mr-1" />}
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Dockets</div>
            <div className="space-y-1.5 mb-2">
              {dockets.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between text-sm bg-white border border-gray-200 rounded-lg px-3 py-1.5"
                >
                  <span className="font-mono text-gray-700">{d.docket_number}</span>
                  <button
                    onClick={() => removeDocket(d.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {!dockets.length && <div className="text-xs text-gray-400">No dockets yet.</div>}
            </div>
            <div className="flex gap-2 items-start">
              <div className="flex-1 min-w-0">
                <SimpleDocketAutocomplete
                  value={newDocket}
                  onChange={setNewDocket}
                  onSubmit={(v) => addDocketValue(v)}
                  placeholder="Search dockets by number or topic (e.g. 02-278, robocalls)"
                />
              </div>
              <button
                onClick={() => addDocketValue(newDocket)}
                disabled={busy}
                className="inline-flex items-center gap-1 text-white text-sm px-3 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 shrink-0"
                style={{ background: accent }}
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              Initial coverage <span className="text-gray-400 font-normal">(how far back the first digest reaches)</span>
            </div>
            <div className="flex items-center gap-2">
              <select
                defaultValue=""
                onChange={(e) => applyInitialWindow(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                <option value="" disabled>Choose a window&hellip;</option>
                <option value="all">Full history</option>
                <option value="90">Last 90 days</option>
                <option value="60">Last 60 days</option>
                <option value="30">Last 30 days</option>
                <option value="7">Last 7 days</option>
              </select>
              {coverageMsg && <span className="text-xs text-gray-500">{coverageMsg}</span>}
            </div>
            <p className="mt-1 text-xs text-gray-400">
              Applies to this client&rsquo;s dockets &mdash; best set before the first digest goes out.
            </p>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              Also send to <span className="text-gray-400 font-normal">(others at the company)</span>
            </div>
            <div className="space-y-1.5 mb-2">
              {recipients.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between text-sm bg-white border border-gray-200 rounded-lg px-3 py-1.5"
                >
                  <span className="text-gray-700">{r.email}</span>
                  <button
                    onClick={() => removeRecipient(r.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {!recipients.length && (
                <div className="text-xs text-gray-400">
                  Just the primary recipient. The digest always goes to the recipient email
                  above; add colleagues here to CC them.
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <input
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                placeholder="colleague@client.com"
                className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
              <button
                onClick={addRecipient}
                disabled={busy}
                className="inline-flex items-center gap-1 text-white text-sm px-3 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 shrink-0"
                style={{ background: accent }}
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Branding — form + live preview
// ---------------------------------------------------------------------------

const BrandingTab: React.FC<{ firmId: string; onSaved: (color?: string) => void }> = ({
  firmId,
  onSaved,
}) => {
  const [b, setB] = useState<Partial<FirmBranding>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    firmService.getBranding(firmId).then((d) => {
      setB(d || {});
      setLoading(false);
    });
  }, [firmId]);

  const set = (k: keyof FirmBranding, v: string) => {
    setB((p) => ({ ...p, [k]: v }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await firmService.updateBranding(firmId, b);
      setSaved(true);
      onSaved(b.primary_color || undefined);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  const color = b.primary_color || DEFAULT_ACCENT;
  const name = b.display_name || 'Your firm';

  return (
    <div className="grid md:grid-cols-2 gap-8 max-w-4xl">
      <div className="space-y-4">
        <Field label="Display name (firm)" value={b.display_name} onChange={(v) => set('display_name', v)} placeholder="Wilkinson Barker Knauer" />
        <Field label="Attorney name (signs + sends the email)" value={b.attorney_name} onChange={(v) => set('attorney_name', v)} placeholder="Nitin Tangellamudi" />
        <Field label="Reply-to email" value={b.reply_to_email} onChange={(v) => set('reply_to_email', v)} placeholder="attorney@firm.com" />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Header color</label>
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : DEFAULT_ACCENT}
              onChange={(e) => set('primary_color', e.target.value)}
              className="w-10 h-10 rounded border border-gray-300 cursor-pointer p-0.5"
            />
            <input
              value={b.primary_color || ''}
              onChange={(e) => set('primary_color', e.target.value)}
              placeholder="#14324f"
              className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-gray-300"
            />
          </div>
        </div>
        <Field label="Logo URL" value={b.logo_url} onChange={(v) => set('logo_url', v)} placeholder="https://…" />
        <Field label="Physical address (required to send)" value={b.address_block} onChange={(v) => set('address_block', v)} />
        <Field label="Disclaimer" value={b.upl_disclaimer} onChange={(v) => set('upl_disclaimer', v)} />
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50"
          style={{ background: color }}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
          {saved ? 'Saved' : 'Save branding'}
        </button>
      </div>

      {/* live preview */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
          What your client sees
        </div>
        <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="px-6 py-6" style={{ background: color }}>
            {b.logo_url ? (
              <img src={b.logo_url} alt={name} className="max-h-9 mb-1.5" />
            ) : (
              <div className="text-white/90 text-sm font-bold">{name}</div>
            )}
            <div className="text-white text-xl font-serif font-bold mt-1">
              Regulatory docket update
            </div>
          </div>
          <div className="p-5 text-sm text-gray-600">
            <div className="font-semibold text-gray-800">3 new filings across 1 docket · Today</div>
            <div className="mt-3 pl-3 border-l-2" style={{ borderColor: color }}>
              <div className="font-semibold text-gray-800">CTIA</div>
              <div className="text-gray-500 mt-1">
                Urges the Commission to adopt the safe-harbor{' '}
                <span style={{ color }} className="font-medium">
                  (page 4)
                </span>
              </div>
            </div>
            {b.upl_disclaimer && (
              <div className="text-[11px] text-gray-400 mt-4 italic">{b.upl_disclaimer}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

const Avatar: React.FC<{ name: string; accent: string }> = ({ name, accent }) => {
  const initials =
    name
      .split(' ')
      .filter((w) => /[a-zA-Z0-9]/.test(w))
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || '?';
  return (
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0"
      style={{ background: accent }}
    >
      {initials}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; accent?: string }> = ({
  label,
  value,
  accent,
}) => (
  <div>
    <div className="text-xl font-bold" style={{ color: accent || '#111827' }}>
      {value}
    </div>
    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
  </div>
);

const Field: React.FC<{
  label: string;
  value?: string | null;
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    <input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
    />
  </div>
);

const Spinner: React.FC = () => (
  <div className="flex justify-center py-20">
    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
  </div>
);

const EmptyState: React.FC<{ icon: any; title: string; body: string }> = ({
  icon: Icon,
  title,
  body,
}) => (
  <div className="text-center py-20 text-gray-500">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gray-100 text-gray-400 mb-4">
      <Icon className="w-6 h-6" />
    </div>
    <div className="font-semibold text-gray-800 text-lg">{title}</div>
    <div className="text-sm mt-1.5 max-w-md mx-auto leading-relaxed">{body}</div>
  </div>
);
